import * as s from "../SystemSpecStyled";
import { useQueries } from "react-query";
import { getSpectrumStats, getSpectrumStatus } from "api/reportAPI";
import { formatBytes, getGBFromByteString } from "utils/functions";
import { useLocation } from "react-router-dom";
import GuageList, { GuageListInterface } from "../GuageList";
import Spinner from "components/atoms/loader/Spinner";
import Storage from "../../Storage";
import NoData from "components/templates/nopage/NoData";
import Professional from "./Professional";
import Enterprises from "./Enterprises";
import { useCallback, useMemo } from "react";
import {
  SpectrumSystemStatsRes,
  SpectrumSystemStatusRes,
} from "api/interfaces/reportInterface.interface";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

type Props = {
  recorderId: string;
};

const initSystemUsage = {
  cpu: 0,
  memory: 0,
  network: 0,
};

const licenseInfoInitial = {
  parameters: {
    cpuArchitecture: "",
    cpuModelName: "",
    networkInterfaces: "",
    physicalMemory: "",
  },
  osInfo: {
    platform: "",
    variant: "",
    variantVersion: "",
  },
};

export default function SystemSpectrumSpec(props: Props): JSX.Element {
  const location = useLocation();
  const isMap = location.pathname.includes("map");

  const [spectrumStatusQuery, spectrumStatsQuery] = useQueries([
    {
      queryKey: ["spectrumStatusQuery", props.recorderId, location.pathname],
      queryFn: () => getSpectrumStatus({ recorderId: props.recorderId }),
      enabled: Boolean(props.recorderId),
      retry: 0,
      staleTime: 30_000,
      cacheTime: 60_000,
      refetchInterval: 30_000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      refetchOnMount: "always",
      select: (res: SpectrumSystemStatusRes) => res?.result ?? null,
    },
    {
      queryKey: ["spectrumStatQuery", props.recorderId, location.pathname],
      queryFn: () => getSpectrumStats({ recorderId: props.recorderId }),
      enabled: Boolean(props.recorderId),
      retry: 0,
      staleTime: 30_000,
      cacheTime: 60_000,
      refetchInterval: 30_000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      refetchOnMount: "always",
      select: (res: SpectrumSystemStatsRes) => res?.result ?? null,
    },
  ]);

  // 초기 로딩: 둘 중 하나라도 최초 로딩이면 스피너
  const isLoading =
    spectrumStatsQuery.isLoading || spectrumStatusQuery.isLoading;
  const refecth = useCallback(() => {
    spectrumStatsQuery.refetch();
    spectrumStatusQuery.refetch();
  }, []);
  useStompDebouncedRefetch({
    stompKey: ["reportDetailUpdated"] as StompDateSignalKey[],
    refetch: refecth,
  });

  const queryData = spectrumStatusQuery.data; // 상태/라이선스
  const statisticData = spectrumStatsQuery.data; // guage

  const toPercent = (v?: number) => (typeof v === "number" ? v * 100 : 0);
  const guageData = useMemo(() => {
    const s = statisticData?.statistics;
    return s
      ? {
          cpu: toPercent(s.cpu),
          memory: toPercent(s.ram),
          network: toPercent(s.network),
        }
      : initSystemUsage;
  }, [statisticData?.statistics]);

  const guageList: GuageListInterface[] = useMemo(
    () => [
      { title: "CPU", value: guageData.cpu },
      { title: "Memory", value: guageData.memory },
      { title: "Network", value: guageData.network },
    ],
    [guageData]
  );

  const licenseDetail = useMemo(() => {
    return queryData?.systemSpec ?? licenseInfoInitial;
  }, [queryData?.systemSpec]);

  if (isLoading) {
    return <Spinner />;
  }
  if (!queryData) {
    return <NoData />;
  }

  return (
    <s.SpectrumInner isMap={isMap}>
      <div className="left inner-box">
        <s.TopContents>
          <li>
            <span className="title">CPU Type: </span>
            {licenseDetail?.parameters?.cpuArchitecture ?? "-"}
          </li>
          <li>
            <span className="title">CPU Model: </span>
            {licenseDetail?.parameters?.cpuModelName ?? "-"}
          </li>
          <li>
            <span className="title">OS: </span>
            {licenseDetail?.osInfo?.platform ?? "-"} /{" "}
            {licenseDetail?.osInfo?.variant ?? "-"} /{" "}
            {licenseDetail?.osInfo?.variantVersion ?? "-"}
          </li>
          <li>
            <span className="title">RAM: </span>
            {String(licenseDetail?.osInfo?.platform ?? "")
              .toLowerCase()
              .includes("linux")
              ? getGBFromByteString(licenseDetail?.parameters?.physicalMemory)
              : formatBytes(licenseDetail?.parameters?.physicalMemory ?? 0, 0)}
          </li>
          <li>
            <span className="title">LAN Card: </span>
            {licenseDetail?.parameters?.networkInterfaces ?? "-"}
          </li>
        </s.TopContents>

        <s.SystemTable>
          {queryData?.licenseType === "enterprise" && queryData?.enterprise ? (
            <Enterprises enterprises={queryData.enterprise} isMap={isMap} />
          ) : (
            <Professional licenses={queryData?.licenses ?? []} isMap={isMap} />
          )}
        </s.SystemTable>
      </div>

      <GuageList guageList={guageList} />
      {isMap && <Storage recorderId={props.recorderId} />}
    </s.SpectrumInner>
  );
}
